import { Link } from 'react-router-dom';
import { Github, Linkedin } from 'lucide-react';
import React, { useRef, useState, useEffect } from 'react';
import { motion, useMotionValue, AnimatePresence } from 'framer-motion';

import { useI18n } from 'src/i18n';

interface NavItem {
  heading: string;
  href: string;
  subheading?: string;
  imgSrc?: string;
}

interface NavLinkProps extends NavItem {
  setIsActive: (isActive: boolean) => void;
  index: number;
}

interface CurvedNavbarProps {
  setIsActive: (isActive: boolean) => void;
  navItems: NavItem[];
}

interface HeaderProps {
  navItems?: NavItem[];
  footer?: React.ReactNode;
}

const MENU_SLIDE_ANIMATION = {
  initial: { x: 'calc(100% + 100px)' },
  enter: { x: '0', transition: { duration: 0.8, ease: [0.76, 0, 0.24, 1] } },
  exit: {
    x: 'calc(100% + 100px)',
    transition: { duration: 0.8, ease: [0.76, 0, 0.24, 1] },
  },
};

const defaultNavItems: NavItem[] = [
  {
    heading: 'Home',
    href: '/',
    subheading: 'Welcome to JobJourney',
  },
  {
    heading: 'Sign In / Up',
    href: '/sign-in',
    subheading: 'Access your account',
  },
  {
    heading: 'Register',
    href: '/register',
    subheading: 'Create an account',
  },
];

const CustomFooter: React.FC = () => (
  <div className="flex w-full justify-center gap-8 px-10 py-5 text-sm text-black md:px-24">
    <a
      href="https://linkedin.com"
      target="_blank"
      rel="noopener noreferrer"
      className="transition-colors hover:text-primary-main"
    >
      <Linkedin size={24} />
    </a>
    <a
      href="https://github.com"
      target="_blank"
      rel="noopener noreferrer"
      className="transition-colors hover:text-primary-main"
    >
      <Github size={24} />
    </a>
  </div>
);

const NavLink: React.FC<NavLinkProps> = ({ heading, href, setIsActive, index }) => {
  const ref = useRef<HTMLAnchorElement | null>(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  const handleMouseMove = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    const rect = ref.current!.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    x.set(mouseX / rect.width - 0.5);
    y.set(mouseY / rect.height - 0.5);
  };

  const handleClick = () => setIsActive(false);

  return (
    <motion.div
      onClick={handleClick}
      initial="initial"
      whileHover="whileHover"
      className="group relative flex items-center justify-between border-b border-black/30 py-4 uppercase transition-colors duration-500 md:py-8"
    >
      <Link ref={ref} onMouseMove={handleMouseMove} to={href}>
        <div className="relative flex items-start">
          <span className="mr-2 text-4xl font-thin text-black transition-colors duration-500">
            {index}.
          </span>
          <div className="flex flex-row gap-2">
            <motion.span
              variants={{
                initial: { x: 0 },
                whileHover: { x: -16 },
              }}
              transition={{
                type: 'spring',
                staggerChildren: 0.075,
                delayChildren: 0.25,
              }}
              className="relative z-10 block text-4xl font-extralight text-black transition-colors duration-500 md:text-4xl"
            >
              {heading.split('').map((letter, i) => (
                <motion.span
                  key={i}
                  variants={{
                    initial: { x: 0 },
                    whileHover: { x: 16 },
                  }}
                  transition={{ type: 'spring' }}
                  className="inline-block"
                >
                  {letter}
                </motion.span>
              ))}
            </motion.span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

const Curve: React.FC = () => {
  const [windowHeight, setWindowHeight] = useState(0);

  useEffect(() => {
    setWindowHeight(window.innerHeight);
    const handleResize = () => setWindowHeight(window.innerHeight);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (windowHeight === 0) return null;

  const initialPath = `M100 0 L200 0 L200 ${windowHeight} L100 ${windowHeight} Q-100 ${windowHeight / 2} 100 0`;
  const targetPath = `M100 0 L200 0 L200 ${windowHeight} L100 ${windowHeight} Q100 ${windowHeight / 2} 100 0`;

  const curve = {
    initial: { d: initialPath },
    enter: {
      d: targetPath,
      transition: { duration: 1, ease: [0.76, 0, 0.24, 1] },
    },
    exit: {
      d: initialPath,
      transition: { duration: 0.8, ease: [0.76, 0, 0.24, 1] },
    },
  };

  return (
    <svg
      className="absolute -left-[99px] top-0 h-full w-[100px] stroke-none"
      style={{ fill: '#ffffff' }}
    >
      <motion.path variants={curve} initial="initial" animate="enter" exit="exit" />
    </svg>
  );
};

const CurvedNavbar: React.FC<CurvedNavbarProps & { footer?: React.ReactNode }> = ({
  setIsActive,
  navItems,
  footer,
}) => {
  const { t } = useI18n();

  return (
    <motion.div
      variants={MENU_SLIDE_ANIMATION}
      initial="initial"
      animate="enter"
      exit="exit"
      className="fixed right-0 top-0 z-[9999] h-[100dvh] w-screen max-w-screen-sm bg-white"
    >
      <div className="flex h-full flex-col overflow-hidden pt-11">
        <div className="mt-0 flex flex-1 flex-col gap-3 overflow-y-auto px-10 text-5xl md:px-24">
          <div className="mb-0 flex shrink-0 items-center justify-between border-b border-black/30 text-sm uppercase text-black">
            <p>{t('menu.navigation')}</p>
            <button
              onClick={() => setIsActive(false)}
              className="flex items-center justify-center rounded p-1 transition-colors hover:bg-gray-100"
              type="button"
              aria-label={t('menu.close')}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </button>
          </div>
          <section className="mt-0 bg-transparent pb-8">
            <div className="mx-auto max-w-7xl">
              {navItems.map((item, index) => (
                <NavLink key={item.href} {...item} setIsActive={setIsActive} index={index + 1} />
              ))}
            </div>
          </section>
        </div>
        <div className="z-10 shrink-0 bg-white">{footer}</div>
      </div>
      <Curve />
    </motion.div>
  );
};

interface CurvedMenuProps {
  isActive: boolean;
  setIsActive: (isActive: boolean) => void;
  navItems?: NavItem[];
  footer?: React.ReactNode;
}

export const CurvedMenu: React.FC<CurvedMenuProps> = ({
  isActive,
  setIsActive,
  navItems = defaultNavItems,
  footer = <CustomFooter />,
}) => (
  <AnimatePresence mode="wait">
    {isActive && (
      <CurvedNavbar setIsActive={setIsActive} navItems={navItems} footer={footer} />
    )}
  </AnimatePresence>
);

export const CurvedMenuHeader: React.FC<HeaderProps> = ({
  navItems = defaultNavItems,
  footer = <CustomFooter />,
}) => {
  const [isActive, setIsActive] = useState(false);

  const handleClick = () => {
    setIsActive(!isActive);
  };

  return (
    <>
      <div className="relative">
        <div
          onClick={handleClick}
          className="fixed right-4 top-14 z-[10000] flex h-10 w-10 cursor-pointer items-center justify-center rounded-lg border border-gray-200 bg-white shadow-md"
        >
          <div className="relative flex h-6 w-8 flex-col items-center justify-between">
            <span
              className={`block h-1 w-7 bg-black transition-transform duration-300 ${isActive ? 'translate-y-2 rotate-45' : ''}`}
            />
            <span
              className={`block h-1 w-7 bg-black transition-opacity duration-300 ${isActive ? 'opacity-0' : ''}`}
            />
            <span
              className={`block h-1 w-7 bg-black transition-transform duration-300 ${isActive ? '-translate-y-3 -rotate-45' : ''}`}
            />
          </div>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {isActive && (
          <CurvedNavbar setIsActive={setIsActive} navItems={navItems} footer={footer} />
        )}
      </AnimatePresence>
    </>
  );
};

export default CurvedMenuHeader;
